import React, { useEffect, useRef, useState } from "react";
import api from "../utils/api";
import {
  LHR_API,
  dedupeSchedules,
  flightNumKey,
  formatScheduleLabel,
  isLikelyFlightNumber,
  mapHeathrowSchedulesToBuckets,
  scheduleAnchorTime,
  scheduleDateKey,
} from "../utils/heathrowFlightSelection";

export default function HeathrowFlightSelector({
  flightNumber,
  setFlightNumber,
  flightDirection,
  setFlightDirection,
  flightDate,
  setFlightDate,
  flightAnchorUtc,
  setFlightAnchorUtc,
  label = "Flight number (Heathrow)",
  placeholder = "e.g. QR111",
  pastHours = 24,
  futureDays = 5,
  note = null,
  selectionRequiredMessage = "Choose the exact Heathrow date and time for this flight.",
}) {
  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightFocused, setFlightFocused] = useState(false);
  const [flightActiveIdx, setFlightActiveIdx] = useState(-1);
  const [flightError, setFlightError] = useState("");
  const [flightSchedules, setFlightSchedules] = useState({ Arrival: [], Departure: [] });
  const [flightDates, setFlightDates] = useState([]);
  const [flightDateError, setFlightDateError] = useState("");
  const flightTimer = useRef(null);
  const flightScheduleTimer = useRef(null);
  const flightScheduleRequestSeq = useRef(0);
  const listboxId = `arrival-assistant-flight-suggestions-${label.replace(/\W+/g, "-").toLowerCase()}`;

  const availableFlightDirections = [
    ...(flightSchedules.Arrival.length ? ["Arrival"] : []),
    ...(flightSchedules.Departure.length ? ["Departure"] : []),
  ];
  const resolvedFlightDirection = availableFlightDirections.includes(flightDirection)
    ? flightDirection
    : availableFlightDirections[0] || "Arrival";
  const shouldShowFlightDirection = availableFlightDirections.length > 1;

  const refreshFlightSchedules = (flightNum, { debounce = true, resetSelection = true, preferredDirection = null } = {}) => {
    const clean = flightNumKey(flightNum);
    if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    if (!clean || clean.length < 2) {
      setFlightSchedules({ Arrival: [], Departure: [] });
      setFlightDates([]);
      setFlightDateError("");
      if (resetSelection) {
        setFlightDirection("Arrival");
        setFlightDate("");
        setFlightAnchorUtc("");
      }
      return;
    }

    const runner = async () => {
      const requestId = ++flightScheduleRequestSeq.current;
      try {
        const lookupResp = await api.get(LHR_API.flightSchedules, {
          params: { flight_number: clean, past_hours: pastHours, future_days: futureDays },
        });
        if (requestId !== flightScheduleRequestSeq.current) return;
        const mapped = mapHeathrowSchedulesToBuckets(lookupResp?.data || null);
        const arrival = dedupeSchedules(mapped.Arrival || [], "Arrival");
        const departure = dedupeSchedules(mapped.Departure || [], "Departure");
        const options = [
          ...(arrival.length ? ["Arrival"] : []),
          ...(departure.length ? ["Departure"] : []),
        ];
        const nextDirection = options.includes(preferredDirection) ? preferredDirection : options[0] || "Arrival";
        setFlightSchedules({ Arrival: arrival, Departure: departure });
        setFlightDirection(nextDirection);
        setFlightDateError(options.length ? "" : "No Heathrow flights found in the recent past or next few days.");
        if (resetSelection) {
          setFlightDate("");
          setFlightAnchorUtc("");
        }
      } catch {
        if (requestId !== flightScheduleRequestSeq.current) return;
        setFlightSchedules({ Arrival: [], Departure: [] });
        setFlightDates([]);
        setFlightDateError("Could not load Heathrow flight times right now.");
        if (resetSelection) {
          setFlightDate("");
          setFlightAnchorUtc("");
        }
      }
    };

    if (debounce) flightScheduleTimer.current = setTimeout(runner, 180);
    else runner();
  };

  const fetchFlightSuggestions = value => {
    const v = flightNumKey(value);
    if (flightTimer.current) clearTimeout(flightTimer.current);
    if (v.length < 2) {
      setFlightSuggestions([]);
      setFlightActiveIdx(-1);
      setFlightError(v.length === 0 ? "" : "Flight number format looks invalid.");
      return;
    }
    if (!isLikelyFlightNumber(v)) {
      setFlightError("Use flight format like BA566 or KU103.");
    } else {
      setFlightError("");
    }
    flightTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get(LHR_API.flightSuggest, { params: { q: v, limit: 8 } });
        const list = resp.data?.suggestions || [];
        setFlightSuggestions(list.map(f => ({ flightNumber: f })));
        setFlightActiveIdx(-1);
      } catch {
        setFlightSuggestions([]);
        setFlightActiveIdx(-1);
      }
    }, 180);
  };

  const selectFlightSuggestion = value => {
    const val = flightNumKey(value);
    setFlightNumber(val);
    setFlightSuggestions([]);
    setFlightActiveIdx(-1);
    setFlightFocused(false);
    setFlightDate("");
    setFlightAnchorUtc("");
    refreshFlightSchedules(val, { debounce: false, resetSelection: true });
  };

  const handleFlightKeyDown = e => {
    if (!flightSuggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFlightActiveIdx(prev => (prev + 1 > flightSuggestions.length - 1 ? 0 : prev + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFlightActiveIdx(prev => (prev - 1 < 0 ? flightSuggestions.length - 1 : prev - 1));
      return;
    }
    if (e.key === "Enter" && flightActiveIdx >= 0) {
      e.preventDefault();
      const candidate = flightSuggestions[flightActiveIdx]?.flightNumber;
      if (candidate) selectFlightSuggestion(candidate);
    }
  };

  useEffect(() => {
    refreshFlightSchedules(flightNumber, { debounce: false, resetSelection: !flightDate || !flightAnchorUtc });
    return () => {
      if (flightTimer.current) clearTimeout(flightTimer.current);
      if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    };
  }, []);

  useEffect(() => {
    const merged = [
      ...(flightSchedules.Arrival || []).map(s => ({ ...s, direction: "Arrival" })),
      ...(flightSchedules.Departure || []).map(s => ({ ...s, direction: "Departure" })),
    ].filter(s => s.direction === resolvedFlightDirection);

    const nextDates = merged.sort(
      (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
    );
    setFlightDates(nextDates);

    if (!flightDate && nextDates.length === 1) {
      const onlyOption = nextDates[0];
      setFlightDirection(onlyOption.direction || resolvedFlightDirection);
      setFlightDate(scheduleDateKey(onlyOption) || "");
      setFlightAnchorUtc(String(scheduleAnchorTime(onlyOption) || ""));
    }

    if (flightDate && nextDates.length) {
      const hasSelection = nextDates.some(s => {
        const optionDate = scheduleDateKey(s);
        const optionAnchor = String(scheduleAnchorTime(s) || "");
        return String(optionDate) === String(flightDate) && String(optionAnchor) === String(flightAnchorUtc || "");
      });
      if (!hasSelection) {
        setFlightDate("");
        setFlightAnchorUtc("");
      }
    }
  }, [flightSchedules, resolvedFlightDirection, flightDate, flightAnchorUtc, setFlightAnchorUtc, setFlightDate, setFlightDirection]);

  return (
    <>
      <div className="form-group">
        <label htmlFor="flightNumber">{label}</label>
        <input
          id="flightNumber"
          type="text"
          value={flightNumber}
          onChange={e => {
            const next = flightNumKey(e.target.value);
            setFlightNumber(next);
            setFlightDate("");
            setFlightAnchorUtc("");
            setFlightFocused(true);
            fetchFlightSuggestions(next);
            refreshFlightSchedules(next, { debounce: true, resetSelection: true });
          }}
          onFocus={() => setFlightFocused(true)}
          onBlur={() => setTimeout(() => setFlightFocused(false), 120)}
          onKeyDown={handleFlightKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={flightFocused && flightSuggestions.length > 0}
          required
        />
        {flightFocused && flightSuggestions.length > 0 && (
          <div className="airport-suggest" role="listbox" id={listboxId}>
            {flightSuggestions.map((item, idx) => (
              <button
                type="button"
                key={`${item.flightNumber}-${idx}`}
                className={`airport-suggest-item ${idx === flightActiveIdx ? "active" : ""}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => selectFlightSuggestion(item.flightNumber)}
              >
                {item.flightNumber}
              </button>
            ))}
          </div>
        )}
        {note && <small className="helper-text">{note}</small>}
        {flightError && <small className="helper-text helper-text--warn">{flightError}</small>}
      </div>

      {shouldShowFlightDirection && (
        <div className="form-group">
          <label htmlFor="flightDirection">Arrival or departure</label>
          <select
            id="flightDirection"
            value={resolvedFlightDirection}
            onChange={e => {
              setFlightDirection(e.target.value);
              setFlightDate("");
              setFlightAnchorUtc("");
            }}
          >
            {availableFlightDirections.map(option => (
              <option key={option} value={option}>
                {option === "Arrival" ? "Arrives at Heathrow" : "Departs from Heathrow"}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="flightDateTime">Flight date / time</label>
        <select
          id="flightDateTime"
          value={flightDate && flightAnchorUtc ? `${flightDate}|${flightAnchorUtc}` : ""}
          onChange={e => {
            const [dateValue, anchorValue] = String(e.target.value || "").split("|");
            setFlightDate(dateValue || "");
            setFlightAnchorUtc(anchorValue || "");
          }}
          required
        >
          <option value="">{selectionRequiredMessage}</option>
          {flightDates.map(s => {
            const dateKey = scheduleDateKey(s);
            const anchor = String(scheduleAnchorTime(s) || "");
            if (!dateKey || !anchor) return null;
            const status = s.status ? ` • ${s.status}` : "";
            const terminal = s.terminal ? ` • T${String(s.terminal).replace(/^T/i, "")}` : "";
            return (
              <option key={`${s.direction}-${dateKey}-${anchor}`} value={`${dateKey}|${anchor}`}>
                {s.direction === "Arrival" ? "Arrives at Heathrow" : "Departs from Heathrow"} — {formatScheduleLabel(anchor)}
                {terminal}
                {status}
              </option>
            );
          })}
        </select>
        {flightDateError && <small className="helper-text helper-text--warn">{flightDateError}</small>}
      </div>
    </>
  );
}
